import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { currencyService } from '../services/currencyService';
import { paymentSelectionService } from '../services/paymentSelectionService';
import { paymentSessionService } from '../services/paymentSessionService';
import { getAllServices, getServiceById, type Service } from '../services/servicesStore';
import { useSiteSettings } from '../services/siteSettingsService';
import { trackEvent } from '../lib/analytics';
import { trackNaturaBusinessEvent } from '../lib/naturaVisitorTracking';
import {
  getSelectedServiceOptions,
  getServiceBillingLabel,
  getServiceOptionKey,
  getServiceOptionsSummary,
  getServicePricePrefix,
  getServiceTotalPrice,
  hasStartingPrice,
  isSingleChoiceService,
} from '../services/serviceCatalogUtils';

const securityLogos = [
  { name: 'SSL', logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRvu4tubg0f1Fah5ytcaKPt_IgveLZcYBSSgg&s' },
  { name: 'Mollie', logo: 'https://www.vectorlogo.zone/logos/mollie/mollie-ar21.svg' },
  { name: 'Klarna', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/40/Klarna_Payment_Badge.svg' }
];

interface CheckoutCreateResponse {
  error?: string;
  paymentUrl?: string;
  providerId?: string;
  providerType?: 'payment' | 'order';
}

const parseCheckoutResponse = (rawText: string): CheckoutCreateResponse | null => {
  if (!rawText) {
    return null;
  }

  try {
    return JSON.parse(rawText) as CheckoutCreateResponse;
  } catch {
    return null;
  }
};

const createCheckoutWithFallback = async (
  payload: Record<string, unknown>
): Promise<CheckoutCreateResponse> => {
  const response = await fetch('/api/payment', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  const rawText = await response.text();
  const data = parseCheckoutResponse(rawText);

  if (response.ok && data?.paymentUrl && data.providerId && data.providerType) {
    return data;
  }

  throw new Error(
    data?.error ||
      (response.ok
        ? 'Le serveur de paiement a renvoyé une réponse invalide'
        : 'Impossible de lancer le paiement')
  );
};

const PaymentPage = () => {
  const { serviceId } = useParams();
  const { settings } = useSiteSettings();
  const [, setDisplayCountry] = useState(currencyService.getCountry());
  const [service, setService] = useState<Service | null>(null);
  const [loading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  // Récupérer le choix de paiement sauvegardé
  const [selectedPayment, setSelectedPayment] = useState<'full' | 'klarna'>(
    paymentSelectionService.getSelection()
  );
  const [selectedOptionKeys, setSelectedOptionKeys] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: currencyService.getCountry()
  });

  useEffect(() => {
    const existing = getServiceById(serviceId || '');
    if (existing) {
      setService(existing);
      setIsLoading(false);
    } else {
      getAllServices().then((data) => {
        const safeData = data || [];
        const foundService = safeData.find((s: any) => s.id === serviceId);
        setService(foundService || null);
        setIsLoading(false);
      });
    }
  }, [serviceId]);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
      setFormData((current) =>
        current.country === nextCountry ? current : { ...current, country: nextCountry }
      );
    });
  }, []);

  useEffect(() => {
    if (!service?.id) {
      return;
    }

    const selection = paymentSelectionService.getCheckoutSelection(service.id);
    setSelectedPayment(selection.paymentType);
    setSelectedOptionKeys(selection.selectedOptionKeys || []);
  }, [service?.id]);

  const selectedOptions = service
    ? getSelectedServiceOptions(service, selectedOptionKeys)
    : [];
  const totalPrice = service ? getServiceTotalPrice(service, selectedOptionKeys) : 0;
  const usesSingleChoiceOptions = service ? isSingleChoiceService(service) : false;
  const selectedPackLabel = usesSingleChoiceOptions ? selectedOptions[0]?.label ?? '' : '';

  useEffect(() => {
    if (!service?.id || !usesSingleChoiceOptions || !service.options?.length || selectedOptionKeys.length > 0) {
      return;
    }

    setSelectedOptionKeys([getServiceOptionKey(service.id, 0)]);
  }, [selectedOptionKeys.length, service?.id, service?.options, usesSingleChoiceOptions]);

  const isStartingPrice = hasStartingPrice(service ?? { pricingMode: 'fixed', options: [] });
  const showKlarna = !isStartingPrice && currencyService.canUseKlarna(
    totalPrice,
    service?.billingType ?? 'one-time'
  );
  const supportsKlarna = currencyService.supportsKlarna();
  const klarnaInstallmentAmount = currencyService.getKlarnaInstallmentAmount(totalPrice);
  const exceedsKlarnaLimit =
    supportsKlarna &&
    service?.billingType === 'one-time' &&
    Number(totalPrice ?? 0) > currencyService.getKlarnaMaxAmountEUR();

  useEffect(() => {
    if (!showKlarna && selectedPayment === 'klarna') {
      setSelectedPayment('full');
    }
  }, [selectedPayment, showKlarna]);

  useEffect(() => {
    if (!service?.id) {
      return;
    }

    paymentSelectionService.saveSelection({
      paymentType: showKlarna && selectedPayment === 'klarna' ? 'klarna' : 'full',
      serviceId: service.id,
      selectedOptionKeys,
    });
  }, [selectedOptionKeys, selectedPayment, service?.id, showKlarna]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (e.target.name === 'country') {
      currencyService.setCountry(e.target.value);
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const createPaymentToken = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `csd_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!service) {
      return;
    }

    setIsProcessing(true);
    const activeService = service;
    const token = createPaymentToken();
    const effectivePaymentType = showKlarna && selectedPayment === 'klarna' ? 'klarna' : 'full';
    const selectedOptionsPayload = selectedOptions.map((option) => ({
      ...option,
      price: currencyService.convertFromEUR(option.price),
      basePriceEUR: option.price,
    }));

    const paymentData = {
      token,
      service: {
        id: activeService.id,
        name: activeService.name,
        price: totalPrice,
        basePrice: activeService.price,
        baseDisplayPrice: currencyService.convertFromEUR(activeService.price),
        billingType: activeService.billingType,
        selectedOptions: selectedOptionsPayload,
      },
      paymentType: effectivePaymentType,
      customer: formData,
      currency: {
        code: currencyService.getCurrencyConfig().code,
        amount: currencyService.convertFromEUR(totalPrice)
      }
    };

    try {
      trackEvent('checkout_submit_attempt', {
        service_id: activeService.id,
        service_name: activeService.name,
        value: currencyService.convertFromEUR(totalPrice),
        currency: currencyService.getCurrencyConfig().code,
        payment_type: effectivePaymentType,
        selected_options_count: selectedOptions.length,
      });
      trackNaturaBusinessEvent('checkout_submit_attempt', {
        service_id: activeService.id,
        service_name: activeService.name,
        value: currencyService.convertFromEUR(totalPrice),
        currency: currencyService.getCurrencyConfig().code,
        payment_type: effectivePaymentType,
        selected_options_count: selectedOptions.length,
      });

      const data = await createCheckoutWithFallback(paymentData);

      const paymentUrl = data?.paymentUrl;
      if (
        paymentUrl &&
        typeof paymentUrl === 'string' &&
        data?.providerId &&
        (data.providerType === 'payment' || data.providerType === 'order')
      ) {
        paymentSessionService.saveSession({
          token,
          providerId: data.providerId,
          providerType: data.providerType,
          serviceId: activeService.id,
          serviceName: activeService.name,
          paymentType: effectivePaymentType,
          amount: currencyService.convertFromEUR(totalPrice),
          currencyCode: currencyService.getCurrencyConfig().code,
          selectedOptions,
          company: '',
          customer: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
          },
          createdAt: new Date().toISOString(),
        });
        paymentSelectionService.clearSelection();
        trackEvent('checkout_redirect_created', {
          service_id: activeService.id,
          service_name: activeService.name,
          value: currencyService.convertFromEUR(totalPrice),
          currency: currencyService.getCurrencyConfig().code,
          payment_type: effectivePaymentType,
          provider_type: data.providerType,
        });
        trackNaturaBusinessEvent('checkout_redirect_created', {
          service_id: activeService.id,
          service_name: activeService.name,
          value: currencyService.convertFromEUR(totalPrice),
          currency: currencyService.getCurrencyConfig().code,
          payment_type: effectivePaymentType,
          provider_type: data.providerType,
        });
        window.location.href = paymentUrl;
        return;
      }

      throw new Error('Invalid payment URL response');
    } catch (error) {
      console.error('Error:', error);
      const message = error instanceof Error ? error.message : 'Une erreur est survenue';
      trackEvent('checkout_submit_error', {
        service_id: activeService.id,
        service_name: activeService.name,
        value: currencyService.convertFromEUR(totalPrice),
        currency: currencyService.getCurrencyConfig().code,
        payment_type: effectivePaymentType,
        reason: message,
      });
      alert(`${message}. Veuillez réessayer.`);
      setIsProcessing(false);
    }
  };

  const formatPrice = (price: number) => currencyService.formatPrice(price);

  const handleToggleOption = (optionIndex: number) => {
    if (!service?.id) {
      return;
    }

    const optionKey = getServiceOptionKey(service.id, optionIndex);
    setSelectedOptionKeys((current) =>
      usesSingleChoiceOptions
        ? (current.includes(optionKey) ? current : [optionKey])
        : current.includes(optionKey)
          ? current.filter((entry) => entry !== optionKey)
          : [...current, optionKey]
    );
  };

  const getOptionPriceLabel = (option: NonNullable<Service['options']>[number]) => {
    const suffix = getOptionBillingText(option.billingType);

    if (option.pricingMode === 'replace-total') {
      return `${formatPrice(option.price)}${suffix}`;
    }

    return `+ ${formatPrice(option.price)}${suffix}`;
  };

  const getOptionBillingText = (billingType?: string) => {
    if (billingType === 'monthly') {
      return '/mois';
    }

    if (billingType === 'days') {
      return '/jour';
    }

    return '';
  };

  const countries = {
    'FR': 'France',
    'BE': 'Belgique',
    'CH': 'Suisse',
    'LU': 'Luxembourg',
    'CA': 'Canada',
    'AR': 'Argentine',
    'BR': 'Brésil',
    'CL': 'Chili',
    'CO': 'Colombie',
    'MX': 'Mexique',
    'PE': 'Pérou',
    'UY': 'Uruguay',
    'US': 'États-Unis',
    'GB': 'Royaume-Uni',
    'DE': 'Allemagne',
    'AT': 'Autriche',
    'DK': 'Danemark',
    'FI': 'Finlande',
    'IE': 'Irlande',
    'IT': 'Italie',
    'NO': 'Norvège',
    'ES': 'Espagne',
    'PT': 'Portugal',
    'NL': 'Pays-Bas',
    'SE': 'Suède',
    'AU': 'Australie',
    'NZ': 'Nouvelle-Zélande',
    'ZA': 'Afrique du Sud',
    'MC': 'Monaco'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-neon-orange border-t-transparent rounded-full animate-spin"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-neon-orange/20 via-transparent to-neon-orange/20 blur-xl animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Service non trouvé</h1>
          <p className="text-gray-600">Le service demandé n'existe pas.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Paiement - {service?.name} - Couteau Suisse Digital</title>
        <meta
          name="description"
          content={`Paiement securise pour ${service?.name}. ${showKlarna ? 'Option Klarna disponible sur ce projet.' : 'Paiement carte bancaire avec configuration adaptee a votre projet.'}`}
        />
      </Helmet>

      <main className="min-h-screen py-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-neon-orange/5 to-transparent"></div>
          <motion.div
            className="absolute top-20 right-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block bg-white/80 backdrop-blur-sm px-6 py-2 rounded-full border border-neon-orange/20 mb-6"
            >
              <span className="text-neon-orange font-medium">Paiement sécurisé</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl font-bold mb-4"
            >
              {service?.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              {service?.description}
            </motion.p>
          </div>

          {/* Form and Summary Grid */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                  <h2 className="text-2xl font-bold mb-6">Informations personnelles</h2>

                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
                        <input
                          type="text"
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 transition-all duration-300"
                      >
                        {Object.entries(countries).map(([code, name]) => (
                          <option key={code} value={code}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                  <h2 className="text-2xl font-bold mb-6">Mode de paiement</h2>

                  {isStartingPrice ? (
                    <div className="mb-8 space-y-4">
                      <div className="rounded-xl border-2 border-neon-orange bg-neon-orange/5 p-4">
                        <div className="text-lg font-semibold mb-1">
                          {usesSingleChoiceOptions ? 'Pack selectionne' : 'Configuration de depart'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {usesSingleChoiceOptions
                            ? (selectedPackLabel || 'Choix du pack en cours')
                            : `Tarif de depart: ${formatPrice(service.price)}`}
                        </div>
                        <div className="text-xl font-bold text-neon-orange">
                          {formatPrice(totalPrice)}
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                          {usesSingleChoiceOptions
                            ? 'Choisissez le pack adapte a votre objectif et le total se met a jour automatiquement.'
                            : selectedOptions.length > 0
                              ? `${selectedOptions.length} option${selectedOptions.length > 1 ? 's' : ''} ajoutee${selectedOptions.length > 1 ? 's' : ''} a votre configuration.`
                              : 'Ajoutez des options si besoin et le total se met a jour directement.'}
                        </p>
                      </div>
                    </div>
                  ) : service.billingType === 'monthly' ? (
                    <div className="mb-8">
                      <div className="p-4 rounded-xl border-2 border-neon-orange bg-neon-orange/5">
                        <div className="text-lg font-semibold mb-1">Abonnement mensuel</div>
                        <div className="text-xl font-bold text-neon-orange">{formatPrice(totalPrice)} /mois</div>
                      </div>
                    </div>
                  ) : service.billingType === 'days' ? (
                    <div className="mb-8">
                      <div className="p-4 rounded-xl border-2 border-neon-orange bg-neon-orange/5">
                        <div className="text-lg font-semibold mb-1">Paiement unique</div>
                        <div className="text-xl font-bold text-neon-orange">{formatPrice(totalPrice)}</div>
                      </div>
                    </div>
                  ) : showKlarna ? (
                    <div className="grid grid-cols-2 gap-6 mb-8">
                      <motion.button
                        type="button"
                        onClick={() => setSelectedPayment('full')}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                          selectedPayment === 'full'
                            ? 'border-neon-orange bg-neon-orange/5'
                            : 'border-gray-200 hover:border-neon-orange/50'
                        }`}
                      >
                        <div className="text-lg font-semibold mb-2">Paiement complet</div>
                        <div className="text-3xl font-bold text-neon-orange">{formatPrice(totalPrice)}</div>
                      </motion.button>

                      <motion.button
                        type="button"
                        onClick={() => setSelectedPayment('klarna')}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                          selectedPayment === 'klarna'
                            ? 'border-neon-orange bg-neon-orange/5'
                            : 'border-gray-200 hover:border-neon-orange/50'
                        }`}
                      >
                        <div className="text-lg font-semibold mb-2">Paiement en 3x</div>
                        <div className="text-3xl font-bold text-neon-orange">{formatPrice(klarnaInstallmentAmount)} /mois</div>
                      </motion.button>
                    </div>
                  ) : (
                    <div className="mb-8">
                      <div className="p-4 rounded-xl border-2 border-neon-orange bg-neon-orange/5">
                        <div className="text-lg font-semibold mb-1">Paiement unique</div>
                        <div className="text-xl font-bold text-neon-orange">{formatPrice(totalPrice)}</div>
                        {exceedsKlarnaLimit && (
                          <p className="mt-3 text-sm text-amber-700">
                            Klarna reste disponible uniquement jusqu a {currencyService.getKlarnaMaxAmountEUR()} EUR par projet.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={isProcessing}
                    whileHover={{ scale: isProcessing ? 1 : 1.02 }}
                    whileTap={{ scale: isProcessing ? 1 : 0.98 }}
                    className={`w-full bg-gradient-to-r relative ${
                      isProcessing
                        ? 'from-gray-400 to-gray-500 cursor-not-allowed'
                        : 'from-neon-orange to-[#FF4500]'
                    } text-white py-4 rounded-xl font-semibold text-lg overflow-hidden group`}
                  >
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      {isProcessing ? (
                        <>
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Traitement en cours...
                        </>
                      ) : (
                        <>
                          {selectedPayment === 'full' || !showKlarna ? (isStartingPrice ? 'Commander la configuration' : 'Payer maintenant') : 'Payer en 3x avec Klarna'}
                        </>
                      )}
                    </span>
                    {!isProcessing && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                        initial={{ x: "-100%" }}
                        whileHover={{ x: "100%" }}
                        transition={{ duration: 1 }}
                      />
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>

            {/* Right Column - Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Order Summary */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h2 className="text-2xl font-bold mb-6">Récapitulatif</h2>

                <div className="space-y-6">
                  <div className="flex justify-between items-center pb-6 border-b border-gray-100">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                        {getServicePricePrefix(service)}
                      </div>
                      <span className="text-lg font-medium">{service?.name}</span>
                    </div>
                    <span className="text-2xl font-bold text-neon-orange">{formatPrice(totalPrice)}</span>
                  </div>

                      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                        <div className="text-sm font-medium text-gray-900">Cadre de l&apos;offre</div>
                        <div className="mt-1 text-sm text-gray-600">{getServiceBillingLabel(service)}</div>
                        {isStartingPrice && (
                          <div className="mt-2 text-sm text-gray-600">
                            {usesSingleChoiceOptions
                              ? `Pack de depart: ${formatPrice(service.price)}. ${selectedPackLabel ? `Pack actif: ${selectedPackLabel}.` : 'Choix du pack en cours.'}`
                              : `Tarif de depart: ${formatPrice(service.price)}. ${selectedOptions.length > 0
                                ? `${selectedOptions.length} option${selectedOptions.length > 1 ? 's' : ''} ajoutee${selectedOptions.length > 1 ? 's' : ''}.`
                                : (getServiceOptionsSummary(service) || 'Options disponibles selon le besoin')}.`}
                          </div>
                        )}
                      </div>

                  <div className="space-y-4">
                    {service?.features.slice(0, 4).map((feature: string, index: number) => (
                      <div key={index} className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-neon-orange flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3 pt-6 border-t border-gray-100">
                    <div className="flex items-center gap-3 text-gray-600">
                      <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Délai : {service?.timeline}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <svg className="w-5 h-5 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Satisfaction garantie</span>
                    </div>
                  </div>

                  {service?.options && service.options.length > 0 && (
                    <div className="space-y-3 pt-6 border-t border-gray-100">
                      <h3 className="text-base font-semibold text-gray-900">
                        {service.optionsTitle || (usesSingleChoiceOptions ? 'Choisissez votre pack' : 'Options fréquentes')}
                      </h3>
                      {service.options.slice(0, 4).map((option, index) => (
                        <button
                          type="button"
                          key={`${service.id}-payment-option-${index}`}
                          onClick={() => handleToggleOption(index)}
                          className={`flex w-full items-start justify-between gap-4 rounded-xl border p-4 text-left transition-all duration-300 ${
                            selectedOptionKeys.includes(getServiceOptionKey(service.id, index))
                              ? 'border-neon-orange bg-neon-orange/5 shadow-sm'
                              : 'border-gray-100 bg-white'
                          }`}
                        >
                          <div>
                            <div className="flex items-center gap-3">
                              <div
                                className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                                  selectedOptionKeys.includes(getServiceOptionKey(service.id, index))
                                    ? 'border-neon-orange bg-neon-orange text-white'
                                    : 'border-gray-300 bg-white text-transparent'
                                }`}
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <div className="font-medium text-gray-900">{option.label}</div>
                            </div>
                            {option.description && (
                              <div className="mt-1 text-sm text-gray-500">{option.description}</div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-neon-orange">
                              {getOptionPriceLabel(option)}
                            </div>
                            <div className="mt-1 text-xs font-medium text-gray-500">
                              {selectedOptionKeys.includes(getServiceOptionKey(service.id, index))
                                ? (usesSingleChoiceOptions ? 'Pack actif' : 'Incluse dans le total')
                                : (usesSingleChoiceOptions ? 'Cliquer pour choisir ce pack' : 'Cliquer pour ajouter')}
                            </div>
                          </div>
                        </button>
                      ))}

                      <div className="rounded-xl border border-neon-orange/20 bg-neon-orange/5 p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Total à régler</div>
                            <div className="mt-1 text-sm text-gray-600">
                              {usesSingleChoiceOptions ? 'Pack de depart' : 'Base'} {formatPrice(service.price)}
                              {selectedOptions.length > 0
                                ? usesSingleChoiceOptions
                                  ? ' avec pack choisi'
                                  : ` + ${selectedOptions.length} option${selectedOptions.length > 1 ? 's' : ''}`
                                : ''}
                            </div>
                          </div>
                          <div className="text-right text-2xl font-bold text-neon-orange">
                            {formatPrice(totalPrice)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Security Badges */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h2 className="text-xl font-bold mb-6">Paiement 100% sécurisé</h2>
                <div className="grid grid-cols-4 gap-6">
                  {securityLogos.map((logo) => (
                    <motion.div
                      key={logo.name}
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center justify-center bg-gray-50 rounded-xl p-4"
                    >
                      <img
                        src={logo.logo}
                        alt={logo.name}
                        loading="lazy"
                        decoding="async"
                        className="h-8 object-contain grayscale hover:grayscale-0 transition-all duration-300"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Support Info */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-neon-orange/10 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold">Besoin d'aide ?</h3>
                    <p className="text-gray-600">Notre équipe est là pour vous</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <a href={settings.phoneHref} className="flex items-center gap-3 text-gray-600 hover:text-neon-orange transition-colors">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {settings.phoneDisplay}
                  </a>
                  <a href={`mailto:${settings.supportEmail}`} className="flex items-center gap-3 text-gray-600 hover:text-neon-orange transition-colors">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {settings.supportEmail}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </>
  );
};

export default PaymentPage;
